import { useState, useMemo, useCallback, useRef, useEffect, type CSSProperties } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { NavIcon, useBrandTokens } from '../../../shell/shared';
import { usePowerSyncDb, usePowerSyncQuery } from '../../../adapters/powersync';
import {
  qFormInstanceById,
  qFormInstanceValues,
  qFormInstanceSignatures,
  qFormDefinitionById,
  qFormTemplatePages,
  qFormTemplateMappings,
  qPatientById,
  qPatientContact,
  qEncounterById,
  parseFieldsSchemaV1,
  flattenFields,
  type FormInstanceListRow,
  type FormInstanceValueRow,
  type FormInstanceSignatureRow,
  type FormDefinitionRow,
  type FormTemplatePageRow,
  type FormTemplateMappingRow,
  type FormFieldDef,
  type FormFieldsSchemaV1,
  type FormSection,
  type BindingConfig,
  type PatientDetailRow,
  type PatientContactRow,
  type PatientEncounterRow,
} from '@helios/data-access';

// ─── Helpers ───

// ─── Signature Pad (minimal canvas) ───

function SignaturePad({
  fieldKey,
  existingData,
  onSave,
  readOnly,
}: {
  fieldKey: string;
  existingData: string | null;
  onSave: (fieldKey: string, dataUrl: string, strokeData: string) => void;
  readOnly: boolean;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [drawing, setDrawing] = useState(false);
  const [hasContent, setHasContent] = useState(false);
  const strokesRef = useRef<Array<{ x: number; y: number }[]>>([]);
  const currentStrokeRef = useRef<{ x: number; y: number }[]>([]);

  // Load existing signature
  useEffect(() => {
    if (existingData && canvasRef.current) {
      try {
        const parsed = JSON.parse(existingData);
        if (parsed.strokes && canvasRef.current) {
          strokesRef.current = parsed.strokes;
          redraw();
          setHasContent(true);
        }
      } catch {
        // ignore
      }
    }
  }, [existingData]);

  const getPos = (e: React.PointerEvent): { x: number; y: number } => {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    return {
      x: (e.clientX - rect.left) * (canvas.width / rect.width),
      y: (e.clientY - rect.top) * (canvas.height / rect.height),
    };
  };

  const redraw = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = '#111';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    for (const stroke of strokesRef.current) {
      if (stroke.length < 2) continue;
      ctx.beginPath();
      ctx.moveTo(stroke[0].x, stroke[0].y);
      for (let i = 1; i < stroke.length; i++) {
        ctx.lineTo(stroke[i].x, stroke[i].y);
      }
      ctx.stroke();
    }
  };

  const handlePointerDown = (e: React.PointerEvent) => {
    if (readOnly) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    if (canvas) canvas.setPointerCapture(e.pointerId);
    setDrawing(true);
    currentStrokeRef.current = [getPos(e)];
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (!drawing || readOnly) return;
    e.preventDefault();
    const pos = getPos(e);
    currentStrokeRef.current.push(pos);
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const pts = currentStrokeRef.current;
    if (pts.length < 2) return;
    ctx.strokeStyle = '#111';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(pts[pts.length - 2].x, pts[pts.length - 2].y);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
  };

  const handlePointerUp = (e: React.PointerEvent) => {
    if (!drawing) return;
    e.preventDefault();
    setDrawing(false);
    if (currentStrokeRef.current.length > 1) {
      strokesRef.current.push([...currentStrokeRef.current]);
      setHasContent(true);
      // Auto-save
      const strokeData = JSON.stringify({ version: 1, strokes: strokesRef.current });
      const dataUrl = canvasRef.current?.toDataURL('image/png') ?? '';
      onSave(fieldKey, dataUrl, strokeData);
    }
    currentStrokeRef.current = [];
  };

  const handleClear = () => {
    strokesRef.current = [];
    currentStrokeRef.current = [];
    setHasContent(false);
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx?.clearRect(0, 0, canvas.width, canvas.height);
    }
    onSave(fieldKey, '', '{}');
  };

  return (
    <div>
      <canvas
        ref={canvasRef}
        width={500}
        height={150}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerLeave={handlePointerUp}
        style={{
          width: '100%',
          maxWidth: 500,
          height: 150,
          border: readOnly ? 'none' : '1px solid var(--border)',
          borderRadius: 'var(--radius-md)',
          cursor: readOnly ? 'default' : 'crosshair',
          touchAction: 'none',
          background: readOnly ? 'transparent' : '#fff',
          display: 'block',
        }}
      />
      {!readOnly && hasContent && (
        <button
          onClick={handleClear}
          style={{
            marginTop: 6,
            background: 'none',
            border: 'none',
            color: 'var(--color-danger-text, #dc2626)',
            fontSize: 'var(--font-size-xs)',
            cursor: 'pointer',
            padding: 0,
          }}
        >
          Clear signature
        </button>
      )}
    </div>
  );
}

// ─── Field Renderer ───

function FieldInput({
  field,
  value,
  onChange,
  readOnly,
}: {
  field: FormFieldDef;
  value: string;
  onChange: (val: string) => void;
  readOnly: boolean;
}) {
  const baseStyle: CSSProperties = {
    width: '100%',
    height: 36,
    padding: '0 10px',
    borderRadius: 'var(--radius-md)',
    border: '1px solid var(--border)',
    background: readOnly ? 'var(--bg-surface-2)' : 'var(--bg-surface)',
    fontSize: 'var(--font-size-sm)',
    color: readOnly ? 'var(--text-muted)' : 'var(--text)',
    outline: 'none',
    boxSizing: 'border-box' as const,
  };

  switch (field.type) {
    case 'text':
      return (
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          readOnly={readOnly}
          placeholder={field.helpText}
          style={baseStyle}
        />
      );
    case 'textarea':
      return (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          readOnly={readOnly}
          placeholder={field.helpText}
          rows={3}
          style={{
            ...baseStyle,
            height: 'auto',
            minHeight: 72,
            padding: '8px 10px',
            resize: 'vertical',
          }}
        />
      );
    case 'number':
      return (
        <input
          type="number"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          readOnly={readOnly}
          placeholder={field.helpText}
          style={baseStyle}
        />
      );
    case 'date':
      return (
        <input
          type="date"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          readOnly={readOnly}
          style={baseStyle}
        />
      );
    case 'select':
      return (
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={readOnly}
          style={baseStyle}
        >
          <option value="">Select...</option>
          {(field.options ?? []).map((opt) => (
            <option key={opt.id} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      );
    case 'multi-select': {
      const selected = value
        ? value.startsWith('[')
          ? (JSON.parse(value) as string[])
          : [value]
        : [];
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {(field.options ?? []).map((opt) => {
            const checked = selected.includes(opt.value);
            return (
              <label
                key={opt.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  cursor: readOnly ? 'default' : 'pointer',
                  fontSize: 'var(--font-size-sm)',
                  color: 'var(--text)',
                }}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  disabled={readOnly}
                  onChange={() => {
                    const next = checked
                      ? selected.filter((v) => v !== opt.value)
                      : [...selected, opt.value];
                    onChange(JSON.stringify(next));
                  }}
                />
                {opt.label}
              </label>
            );
          })}
        </div>
      );
    }
    case 'yes-no':
      return (
        <div style={{ display: 'flex', gap: 16 }}>
          {['yes', 'no'].map((opt) => (
            <label
              key={opt}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                cursor: readOnly ? 'default' : 'pointer',
                fontSize: 'var(--font-size-sm)',
                color: 'var(--text)',
              }}
            >
              <input
                type="radio"
                name={field.key}
                value={opt}
                checked={value === opt}
                disabled={readOnly}
                onChange={(e) => onChange(e.target.value)}
                style={{ accentColor: 'var(--color-primary)' }}
              />
              {opt.charAt(0).toUpperCase() + opt.slice(1)}
            </label>
          ))}
        </div>
      );
    case 'binding': {
      const cfg = field.config as BindingConfig | undefined;
      return (
        <div
          style={{
            ...baseStyle,
            display: 'flex',
            alignItems: 'center',
            color: 'var(--text-muted)',
            fontStyle: 'italic',
          }}
        >
          Auto-filled: {cfg ? `${cfg.source}.${cfg.path}` : '—'}
        </div>
      );
    }
    default:
      return (
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          readOnly={readOnly}
          style={baseStyle}
        />
      );
  }
}

// ─── Section Renderer ───

function SectionRenderer({
  section,
  values,
  signatures,
  onValueChange,
  onSignatureSave,
  readOnly,
  bindingData,
}: {
  section: FormSection;
  values: Record<string, string>;
  signatures: Record<string, string>;
  onValueChange: (fieldKey: string, val: string) => void;
  onSignatureSave: (fieldKey: string, dataUrl: string, strokeData: string) => void;
  readOnly: boolean;
  bindingData: Record<string, string>;
}) {
  return (
    <div
      style={{
        background: 'var(--bg-surface-2, #f9fafb)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-lg)',
        padding: 'var(--sp-5)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--sp-1)',
      }}
    >
      <h3
        style={{
          margin: 0,
          fontSize: 'var(--font-size-lg)',
          fontWeight: 'var(--font-weight-semi)',
          color: 'var(--text)',
        }}
      >
        {section.title || 'Section'}
      </h3>
      {section.description && (
        <p style={{ margin: 0, fontSize: 'var(--font-size-sm)', color: 'var(--text-muted)' }}>
          {section.description}
        </p>
      )}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 'var(--sp-4)',
          marginTop: 'var(--sp-3)',
        }}
      >
        {section.fields.map((field) => {
          const isFullWidth =
            field.layout === 'full' ||
            field.type === 'textarea' ||
            field.type === 'signature' ||
            field.type === 'drawing';
          return (
            <div
              key={field.key}
              style={{
                gridColumn: isFullWidth ? '1 / -1' : undefined,
                display: 'flex',
                flexDirection: 'column',
                gap: 'var(--sp-2)',
              }}
            >
              <label
                style={{
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 'var(--font-weight-medium)',
                  color: 'var(--text)',
                }}
              >
                {field.label || field.key}
                {field.required && (
                  <span style={{ color: 'var(--color-danger-text, #dc2626)' }}> *</span>
                )}
              </label>

              {field.type === 'signature' ? (
                <SignaturePad
                  fieldKey={field.key}
                  existingData={signatures[field.key] ?? null}
                  onSave={onSignatureSave}
                  readOnly={readOnly}
                />
              ) : field.type === 'drawing' ? (
                <div
                  style={{
                    border: '2px dashed rgba(22,163,74,0.4)',
                    borderRadius: 'var(--radius-md)',
                    padding: 'var(--sp-4)',
                    textAlign: 'center',
                    color: '#16a34a',
                    fontSize: 'var(--font-size-sm)',
                    background: 'rgba(22,163,74,0.04)',
                    minHeight: 80,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  Drawing area (coming soon)
                </div>
              ) : field.type === 'binding' ? (
                <div
                  style={{
                    width: '100%',
                    height: 36,
                    padding: '0 10px',
                    borderRadius: 'var(--radius-md)',
                    border: '1px solid var(--border)',
                    background: 'var(--bg-surface-2)',
                    fontSize: 'var(--font-size-sm)',
                    color: 'var(--text)',
                    display: 'flex',
                    alignItems: 'center',
                    boxSizing: 'border-box' as const,
                  }}
                >
                  {(() => {
                    const cfg = field.config as BindingConfig | undefined;
                    if (!cfg) return '—';
                    const key = `${cfg.source}.${cfg.path}`;
                    return bindingData[key] || '—';
                  })()}
                </div>
              ) : (
                <FieldInput
                  field={field}
                  value={values[field.key] ?? ''}
                  onChange={(val) => onValueChange(field.key, val)}
                  readOnly={readOnly}
                />
              )}

              {field.helpText &&
                field.type !== 'text' &&
                field.type !== 'textarea' &&
                field.type !== 'number' && (
                  <span
                    style={{
                      fontSize: 'var(--font-size-xs)',
                      color: 'var(--text-placeholder, #9ca3af)',
                    }}
                  >
                    {field.helpText}
                  </span>
                )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Template Overlay View (Render mode) ───

function TemplateOverlayView({
  pages,
  mappings,
  fields,
  values,
  signatures,
  schema,
  bindingData,
  onValueChange,
  onSignatureSave,
  readOnly,
}: {
  pages: FormTemplatePageRow[];
  mappings: FormTemplateMappingRow[];
  fields: FormFieldDef[];
  values: Record<string, string>;
  signatures: Record<string, string>;
  schema: FormFieldsSchemaV1;
  bindingData: Record<string, string>;
  onValueChange: (fieldKey: string, val: string) => void;
  onSignatureSave: (fieldKey: string, dataUrl: string, strokeData: string) => void;
  readOnly: boolean;
}) {
  const [selectedPageIdx, setSelectedPageIdx] = useState(0);
  const [imageError, setImageError] = useState(false);
  const [imageLoading, setImageLoading] = useState(true);
  const [editingField, setEditingField] = useState<{ key: string; mappingId: string } | null>(null);
  const [editValue, setEditValue] = useState('');
  const [sigModalField, setSigModalField] = useState<string | null>(null);
  const currentPage = pages[selectedPageIdx];
  const pageMappings = useMemo(
    () => (currentPage ? mappings.filter((m) => m.page_id === currentPage.id) : []),
    [mappings, currentPage],
  );

  // Reset states when page changes
  useEffect(() => {
    setImageError(false);
    setImageLoading(true);
  }, [selectedPageIdx]);

  // If no pages or images failed to load → show values summary
  if (pages.length === 0 || imageError) {
    return (
      <div style={{ padding: 'var(--sp-5)' }}>
        {pages.length > 0 && imageError && (
          <div
            style={{
              padding: 'var(--sp-3)',
              marginBottom: 'var(--sp-4)',
              borderRadius: 'var(--radius-md)',
              background: 'var(--color-warning-bg, #fefce8)',
              border: '1px solid var(--color-warning-text, #a16207)',
              color: 'var(--color-warning-text, #a16207)',
              fontSize: 'var(--font-size-sm)',
            }}
          >
            Template images are unavailable (they were uploaded in a previous session). Showing
            values summary instead.
          </div>
        )}
        <div
          style={{ maxWidth: 720, display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)' }}
        >
          {schema.sections.map((section) => (
            <div
              key={section.id}
              style={{
                background: 'var(--bg-surface-2, #f9fafb)',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-lg)',
                padding: 'var(--sp-5)',
              }}
            >
              <h3
                style={{
                  margin: '0 0 var(--sp-3)',
                  fontSize: 'var(--font-size-lg)',
                  fontWeight: 'var(--font-weight-semi)',
                  color: 'var(--text)',
                }}
              >
                {section.title || 'Section'}
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
                {section.fields.map((field) => {
                  const val = resolveInstanceValue(field, values, signatures, bindingData);
                  const isSig = field.type === 'signature';
                  return (
                    <div
                      key={field.key}
                      style={{ display: 'flex', gap: 'var(--sp-3)', alignItems: 'flex-start' }}
                    >
                      <span
                        style={{
                          fontSize: 'var(--font-size-sm)',
                          fontWeight: 'var(--font-weight-medium)',
                          color: 'var(--text-muted)',
                          minWidth: 140,
                          flexShrink: 0,
                        }}
                      >
                        {field.label || field.key}
                      </span>
                      {isSig && signatures[field.key] ? (
                        <SignaturePreview
                          strokeData={signatures[field.key]}
                          width={200}
                          height={60}
                        />
                      ) : (
                        <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text)' }}>
                          {val}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  const hasPrev = selectedPageIdx > 0;
  const hasNext = selectedPageIdx < pages.length - 1;

  const pageNavBtnStyle = (disabled: boolean): CSSProperties => ({
    height: 32,
    padding: '0 14px',
    fontSize: 'var(--font-size-xs)',
    fontWeight: 'var(--font-weight-medium)' as const,
    border: '1px solid var(--border)',
    borderRadius: 'var(--radius-sm)',
    background: disabled ? 'var(--bg-surface-2)' : 'var(--bg-surface)',
    color: disabled ? 'var(--text-placeholder, #9ca3af)' : 'var(--text)',
    cursor: disabled ? 'default' : 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    opacity: disabled ? 0.5 : 1,
  });

  const PageNav = () => (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 'var(--sp-3)',
      }}
    >
      <button
        disabled={!hasPrev}
        onClick={() => hasPrev && setSelectedPageIdx(selectedPageIdx - 1)}
        style={pageNavBtnStyle(!hasPrev)}
      >
        <NavIcon name="chevron-left" /> Previous
      </button>
      <div style={{ display: 'flex', gap: 4 }}>
        {pages.map((p, i) => (
          <button
            key={p.id}
            onClick={() => setSelectedPageIdx(i)}
            style={{
              width: 28,
              height: 28,
              borderRadius: 'var(--radius-sm)',
              border: '1px solid var(--border)',
              background: i === selectedPageIdx ? 'var(--color-primary)' : 'var(--bg-surface)',
              color: i === selectedPageIdx ? '#fff' : 'var(--text)',
              fontSize: 'var(--font-size-xs)',
              fontWeight: 'var(--font-weight-medium)' as const,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {p.page_number}
          </button>
        ))}
      </div>
      <button
        disabled={!hasNext}
        onClick={() => hasNext && setSelectedPageIdx(selectedPageIdx + 1)}
        style={pageNavBtnStyle(!hasNext)}
      >
        Next <NavIcon name="chevron-right" />
      </button>
    </div>
  );

  return (
    <div
      style={{
        padding: 'var(--sp-5)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--sp-4)',
      }}
    >
      {/* Top page navigation */}
      {pages.length > 1 && <PageNav />}

      {/* Template with overlaid values */}
      {currentPage && (
        <div style={{ position: 'relative', display: 'inline-block', alignSelf: 'center' }}>
          <style>{`@keyframes _form_spin { to { transform: rotate(360deg) } }`}</style>
          <img
            src={currentPage.image_url}
            alt={`Page ${currentPage.page_number}`}
            style={{
              maxWidth: '100%',
              display: 'block',
              borderRadius: 'var(--radius-md)',
              border: '1px solid var(--border)',
            }}
            onLoad={() => setImageLoading(false)}
            onError={() => {
              setImageLoading(false);
              setImageError(true);
            }}
          />
          {imageLoading && (
            <div
              style={{
                position: 'absolute',
                inset: 0,
                background: 'rgba(240,240,240,0.7)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                borderRadius: 'var(--radius-md)',
                color: 'var(--text-muted)',
                fontSize: 'var(--font-size-sm)',
                zIndex: 10,
              }}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                style={{ animation: '_form_spin 1s linear infinite' }}
              >
                <path d="M21 12a9 9 0 1 1-6.219-8.56" />
              </svg>
              Loading...
            </div>
          )}
          {pageMappings.map((m) => {
            const field = fields.find((f) => f.key === m.field_key);
            if (!field) return null;
            const isSignature = field.type === 'signature';
            const isDrawing = field.type === 'drawing';
            const isBinding = field.type === 'binding';
            const isEditable = !readOnly && !isBinding && !isDrawing;
            const val = resolveInstanceValue(field, values, signatures, bindingData);
            const isEditingThis =
              editingField?.key === field.key && editingField?.mappingId === m.id;

            return (
              <div
                key={m.id}
                style={{
                  position: 'absolute',
                  left: `${m.x_pct}%`,
                  top: `${m.y_pct}%`,
                  width: `${m.w_pct}%`,
                  height: `${m.h_pct}%`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 11,
                  color: isDrawing ? '#16a34a' : isSignature ? '#dc2626' : '#111',
                  fontWeight: 500,
                  overflow: isEditingThis ? 'visible' : 'hidden',
                  padding: 2,
                  background: readOnly
                    ? 'transparent'
                    : isDrawing
                      ? 'rgba(22,163,74,0.08)'
                      : isSignature
                        ? 'rgba(220,38,38,0.06)'
                        : 'transparent',
                  border: readOnly
                    ? 'none'
                    : isDrawing
                      ? '2px dashed rgba(22,163,74,0.4)'
                      : isSignature
                        ? '2px dashed rgba(220,38,38,0.4)'
                        : isEditingThis
                          ? '2px solid var(--color-primary, #2563eb)'
                          : isEditable
                            ? '1px dashed rgba(37,99,235,0.35)'
                            : undefined,
                  borderRadius: isDrawing || isSignature || isEditable ? 4 : 1,
                  cursor: isEditable ? 'pointer' : 'default',
                  zIndex: isEditingThis ? 20 : undefined,
                }}
                onClick={() => {
                  if (!isEditable) return;
                  if (isSignature) {
                    setSigModalField(field.key);
                  } else {
                    setEditingField({ key: field.key, mappingId: m.id });
                    setEditValue(values[field.key] ?? '');
                  }
                }}
              >
                {isSignature && signatures[field.key] ? (
                  <SignaturePreview strokeData={signatures[field.key]} width={100} height={40} />
                ) : (
                  val
                )}

                {/* Pencil icon for editable fields */}
                {isEditable && !isEditingThis && (
                  <span
                    style={{
                      position: 'absolute',
                      right: -1,
                      top: -1,
                      width: 14,
                      height: 14,
                      borderRadius: '0 4px 0 4px',
                      background: 'rgba(37,99,235,0.35)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <svg
                      width="8"
                      height="8"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#fff"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M17 3a2.85 2.85 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
                    </svg>
                  </span>
                )}

                {/* Inline popover editor */}
                {isEditingThis && !isSignature && (
                  <div
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      marginTop: 4,
                      background: '#fff',
                      border: '1px solid var(--border)',
                      borderRadius: 'var(--radius-md)',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                      padding: 'var(--sp-3)',
                      zIndex: 30,
                      minWidth: 220,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 'var(--sp-2)',
                    }}
                  >
                    <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)' }}>
                      {field.label || field.key}
                    </label>
                    {field.type === 'textarea' ? (
                      <textarea
                        autoFocus
                        rows={3}
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        style={{
                          width: '100%',
                          fontSize: 13,
                          padding: '6px 8px',
                          border: '1px solid var(--border)',
                          borderRadius: 'var(--radius-sm)',
                          resize: 'vertical',
                          fontFamily: 'inherit',
                        }}
                      />
                    ) : field.type === 'select' || field.type === 'multi-select' ? (
                      <select
                        autoFocus
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        style={{
                          width: '100%',
                          fontSize: 13,
                          padding: '6px 8px',
                          height: 32,
                          border: '1px solid var(--border)',
                          borderRadius: 'var(--radius-sm)',
                        }}
                      >
                        <option value="">— Select —</option>
                        {(field.options ?? []).map((opt) => (
                          <option key={opt.id} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    ) : field.type === 'yes-no' ? (
                      <div style={{ display: 'flex', gap: 8 }}>
                        {['yes', 'no'].map((opt) => (
                          <label
                            key={opt}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 4,
                              fontSize: 13,
                              cursor: 'pointer',
                            }}
                          >
                            <input
                              type="radio"
                              name={`pin_${field.key}`}
                              value={opt}
                              checked={editValue === opt}
                              onChange={() => setEditValue(opt)}
                              style={{ accentColor: 'var(--color-primary)' }}
                            />
                            {opt.charAt(0).toUpperCase() + opt.slice(1)}
                          </label>
                        ))}
                      </div>
                    ) : (
                      <input
                        autoFocus
                        type={
                          field.type === 'number'
                            ? 'number'
                            : field.type === 'date'
                              ? 'date'
                              : 'text'
                        }
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        style={{
                          width: '100%',
                          fontSize: 13,
                          padding: '6px 8px',
                          height: 32,
                          border: '1px solid var(--border)',
                          borderRadius: 'var(--radius-sm)',
                          boxSizing: 'border-box',
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            onValueChange(field.key, editValue);
                            setEditingField(null);
                          } else if (e.key === 'Escape') {
                            setEditingField(null);
                          }
                        }}
                      />
                    )}
                    <div
                      style={{ display: 'flex', gap: 'var(--sp-2)', justifyContent: 'flex-end' }}
                    >
                      <button
                        onClick={() => {
                          onValueChange(field.key, '');
                          setEditValue('');
                          setEditingField(null);
                        }}
                        style={{
                          height: 28,
                          padding: '0 10px',
                          fontSize: 12,
                          border: '1px solid var(--border)',
                          borderRadius: 'var(--radius-sm)',
                          background: 'var(--bg-surface)',
                          cursor: 'pointer',
                          color: 'var(--text-muted)',
                        }}
                      >
                        Clear
                      </button>
                      <button
                        onClick={() => {
                          onValueChange(field.key, editValue);
                          setEditingField(null);
                        }}
                        style={{
                          height: 28,
                          padding: '0 10px',
                          fontSize: 12,
                          border: 'none',
                          borderRadius: 'var(--radius-sm)',
                          background: 'var(--color-primary, #2563eb)',
                          color: '#fff',
                          cursor: 'pointer',
                          fontWeight: 600,
                        }}
                      >
                        Save
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}

          {/* Click-away to close popover */}
          {editingField && (
            <div
              style={{ position: 'fixed', inset: 0, zIndex: 15 }}
              onClick={() => setEditingField(null)}
            />
          )}
        </div>
      )}

      {/* Bottom page navigation */}
      {pages.length > 1 && <PageNav />}

      {/* Signature Modal */}
      {sigModalField && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 100,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onClick={() => setSigModalField(null)}
        >
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.4)' }} />
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'relative',
              background: '#fff',
              borderRadius: 'var(--radius-lg)',
              padding: 'var(--sp-5)',
              width: 540,
              maxWidth: '90vw',
              boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--sp-3)',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3
                style={{
                  margin: 0,
                  fontSize: 'var(--font-size-lg)',
                  fontWeight: 'var(--font-weight-semi)',
                }}
              >
                {fields.find((f) => f.key === sigModalField)?.label || 'Signature'}
              </h3>
              <button
                onClick={() => setSigModalField(null)}
                style={{
                  width: 28,
                  height: 28,
                  border: 'none',
                  background: 'none',
                  cursor: 'pointer',
                  fontSize: 18,
                  color: 'var(--text-muted)',
                }}
              >
                ×
              </button>
            </div>
            <SignaturePad
              fieldKey={sigModalField}
              existingData={signatures[sigModalField] ?? null}
              onSave={(fk, du, sd) => {
                onSignatureSave(fk, du, sd);
                setSigModalField(null);
              }}
              readOnly={false}
            />
          </div>
        </div>
      )}
    </div>
  );
}

function SignaturePreview({
  strokeData,
  width,
  height,
}: {
  strokeData: string;
  width: number;
  height: number;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    try {
      const parsed = JSON.parse(strokeData);
      if (!parsed.strokes) return;
      // Scale strokes from 500x150 source to preview size
      const sx = canvas.width / 500;
      const sy = canvas.height / 150;
      ctx.strokeStyle = '#111';
      ctx.lineWidth = 1;
      ctx.lineCap = 'round';
      for (const stroke of parsed.strokes) {
        if (stroke.length < 2) continue;
        ctx.beginPath();
        ctx.moveTo(stroke[0].x * sx, stroke[0].y * sy);
        for (let i = 1; i < stroke.length; i++) {
          ctx.lineTo(stroke[i].x * sx, stroke[i].y * sy);
        }
        ctx.stroke();
      }
    } catch {
      /* ignore */
    }
  }, [strokeData, width, height]);

  return (
    <canvas
      ref={canvasRef}
      width={width}
      height={height}
      style={{ width: '100%', height: '100%', display: 'block' }}
    />
  );
}

function resolveInstanceValue(
  field: FormFieldDef | undefined,
  values: Record<string, string>,
  signatures: Record<string, string>,
  bindingData?: Record<string, string>,
): string {
  if (!field) return '—';
  if (field.type === 'drawing') return field.label || 'Drawing';
  if (field.type === 'signature') return signatures[field.key] ? '(signed)' : '(unsigned)';
  if (field.type === 'binding') {
    const cfg = field.config as BindingConfig | undefined;
    if (cfg && bindingData) {
      const key = `${cfg.source}.${cfg.path}`;
      return bindingData[key] || '—';
    }
    return '—';
  }
  return values[field.key] || '—';
}

// ─── Mode Switch ───

function ModeSwitch({
  mode,
  onChange,
  primary,
}: {
  mode: 'form' | 'render';
  onChange: (m: 'form' | 'render') => void;
  primary: string;
}) {
  return (
    <div style={{ display: 'flex', gap: 0 }}>
      {(['render', 'form'] as const).map((m) => {
        const active = mode === m;
        return (
          <button
            key={m}
            onClick={() => onChange(m)}
            style={{
              height: 30,
              padding: '0 12px',
              border: '1px solid var(--border)',
              background: active ? primary : 'var(--bg-surface)',
              color: active ? '#fff' : 'var(--text)',
              fontSize: 'var(--font-size-xs)',
              fontWeight: 'var(--font-weight-medium)',
              cursor: 'pointer',
              borderRadius:
                m === 'render'
                  ? 'var(--radius-md) 0 0 var(--radius-md)'
                  : '0 var(--radius-md) var(--radius-md) 0',
              marginLeft: m === 'form' ? -1 : 0,
            }}
          >
            {m === 'render' ? 'Render' : 'Form'}
          </button>
        );
      })}
    </div>
  );
}

// ─── Main Renderer Page ───

export function FormRendererPage() {
  const {
    id: patientId,
    encounterId,
    formInstanceId,
  } = useParams<{
    id: string;
    encounterId: string;
    formInstanceId: string;
  }>();
  const navigate = useNavigate();
  const brandTokens = useBrandTokens();
  const primary = brandTokens['--color-primary'] || '#2563eb';
  const { db } = usePowerSyncDb();

  // ── Data ──
  const { data: instanceRows = [] } = usePowerSyncQuery<FormInstanceListRow>(
    qFormInstanceById(formInstanceId ?? ''),
  );
  const instance = instanceRows[0] ?? null;

  const { data: defRows = [] } = usePowerSyncQuery<FormDefinitionRow>(
    qFormDefinitionById(instance?.form_definition_id ?? ''),
  );
  const definition = defRows[0] ?? null;

  const { data: valueRows = [] } = usePowerSyncQuery<FormInstanceValueRow>(
    qFormInstanceValues(formInstanceId ?? ''),
  );

  const { data: sigRows = [] } = usePowerSyncQuery<FormInstanceSignatureRow>(
    qFormInstanceSignatures(formInstanceId ?? ''),
  );

  // ── Derived ──
  const schema = useMemo<FormFieldsSchemaV1>(() => {
    if (!definition) return { version: 1, sections: [] };
    return parseFieldsSchemaV1(definition.fields_schema);
  }, [definition?.id, definition?.fields_schema]);

  const valuesMap = useMemo(() => {
    const map: Record<string, string> = {};
    for (const v of valueRows) {
      if (v.value !== null) map[v.field_key] = v.value;
    }
    return map;
  }, [valueRows]);

  const sigMap = useMemo(() => {
    const map: Record<string, string> = {};
    for (const s of sigRows) {
      map[s.field_key] = s.stroke_data;
    }
    return map;
  }, [sigRows]);

  // Build a map of valueRow IDs for upsert
  const valueIdMap = useMemo(() => {
    const map: Record<string, string> = {};
    for (const v of valueRows) map[v.field_key] = v.id;
    return map;
  }, [valueRows]);

  const sigIdMap = useMemo(() => {
    const map: Record<string, string> = {};
    for (const s of sigRows) map[s.field_key] = s.id;
    return map;
  }, [sigRows]);

  const isCompleted = instance?.status === 'completed';

  // ── View mode ──
  const defaultView = instance?.default_view === 'render' ? 'render' : 'form';
  const [viewMode, setViewMode] = useState<'form' | 'render'>(defaultView);
  // Override to render for completed forms
  useEffect(() => {
    if (isCompleted) setViewMode('render');
  }, [isCompleted]);
  // Sync default_view when instance loads
  useEffect(() => {
    if (instance && !isCompleted)
      setViewMode(instance.default_view === 'render' ? 'render' : 'form');
  }, [instance?.id]);

  // ── Template data (for render mode) ──
  const { data: templatePages = [] } = usePowerSyncQuery<FormTemplatePageRow>(
    qFormTemplatePages(instance?.form_definition_id ?? ''),
  );
  const { data: templateMappings = [] } = usePowerSyncQuery<FormTemplateMappingRow>(
    qFormTemplateMappings(instance?.form_definition_id ?? ''),
  );
  const allFields = useMemo(() => flattenFields(schema), [schema]);

  // ── Patient + Encounter data (for binding resolution) ──
  const { data: patientRows = [] } = usePowerSyncQuery<PatientDetailRow>(
    qPatientById(patientId ?? ''),
  );
  const patient = patientRows[0] ?? null;

  const { data: contactRows = [] } = usePowerSyncQuery<PatientContactRow>(
    qPatientContact(patientId ?? ''),
  );
  const contact = contactRows[0] ?? null;

  const { data: encounterDetailRows = [] } = usePowerSyncQuery<PatientEncounterRow>(
    qEncounterById(encounterId ?? ''),
  );
  const encounterDetail = encounterDetailRows[0] ?? null;

  // Build binding resolution map from live data
  const bindingData = useMemo<Record<string, string>>(() => {
    const map: Record<string, string> = {};
    if (patient) {
      if (patient.full_name) map['patient.full_name'] = patient.full_name;
      if (patient.birth_date) map['patient.birth_date'] = patient.birth_date;
      if (patient.sex) map['patient.sex'] = patient.sex;
      if (patient.patient_number) map['patient.patient_number'] = patient.patient_number;
      if (patient.blood_group_abo) map['patient.blood_group_abo'] = patient.blood_group_abo;
      if (patient.drug_allergies_notes)
        map['patient.drug_allergies_notes'] = patient.drug_allergies_notes;
      if (patient.medical_notes) map['patient.medical_notes'] = patient.medical_notes;
    }
    if (contact) {
      if (contact.email) map['patient.email'] = contact.email;
      if (contact.mobile_number) {
        const code = contact.mobile_country_code ? `${contact.mobile_country_code} ` : '';
        map['patient.mobile_number'] = `${code}${contact.mobile_number}`;
      }
    }
    if (encounterDetail) {
      if (encounterDetail.type) map['encounter.type'] = encounterDetail.type;
      if (encounterDetail.started_at) map['encounter.started_at'] = encounterDetail.started_at;
      if (encounterDetail.provider_name)
        map['encounter.provider_name'] = encounterDetail.provider_name;
    }
    return map;
  }, [patient, contact, encounterDetail]);

  // ── Handlers ──
  const handleValueChange = useCallback(
    async (fieldKey: string, val: string) => {
      if (!db || !formInstanceId || isCompleted) return;
      const existingId = valueIdMap[fieldKey];
      if (existingId) {
        await db.execute(
          `UPDATE form_instance_values SET value = ?, updated_at = datetime('now') WHERE id = ?`,
          [val, existingId],
        );
      } else {
        await db.execute(
          `INSERT INTO form_instance_values (id, form_instance_id, field_key, value, created_at, updated_at) VALUES (?, ?, ?, ?, datetime('now'), datetime('now'))`,
          [crypto.randomUUID(), formInstanceId, fieldKey, val],
        );
      }
    },
    [db, formInstanceId, valueIdMap, isCompleted],
  );

  const handleSignatureSave = useCallback(
    async (fieldKey: string, _dataUrl: string, strokeData: string) => {
      if (!db || !formInstanceId || isCompleted) return;
      const existingId = sigIdMap[fieldKey];
      if (existingId) {
        await db.execute(
          `UPDATE form_instance_signatures SET stroke_data = ?, updated_at = datetime('now') WHERE id = ?`,
          [strokeData, existingId],
        );
      } else {
        await db.execute(
          `INSERT INTO form_instance_signatures (id, form_instance_id, field_key, stroke_data, created_at, updated_at) VALUES (?, ?, ?, ?, datetime('now'), datetime('now'))`,
          [crypto.randomUUID(), formInstanceId, fieldKey, strokeData],
        );
      }
    },
    [db, formInstanceId, sigIdMap, isCompleted],
  );

  const handleComplete = useCallback(async () => {
    if (!db || !formInstanceId) return;
    await db.execute(
      `UPDATE form_instances SET status = 'completed', completed_at = datetime('now'), updated_at = datetime('now') WHERE id = ?`,
      [formInstanceId],
    );
  }, [db, formInstanceId]);

  const handleCreateNewDraft = useCallback(async () => {
    if (!db || !instance) return;
    const newId = crypto.randomUUID();
    // Duplicate instance as draft
    await db.execute(
      `INSERT INTO form_instances (id, org_id, clinic_id, form_definition_id, patient_id, encounter_id, appointment_id, queue_id, visit_id, status, created_at, updated_at)
       VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, 'draft', datetime('now'), datetime('now'))`,
      [
        newId,
        instance.org_id,
        instance.clinic_id,
        instance.form_definition_id,
        instance.patient_id,
        instance.encounter_id,
        instance.appointment_id,
        instance.queue_id,
        instance.visit_id,
      ],
    );
    // Duplicate values
    for (const v of valueRows) {
      await db.execute(
        `INSERT INTO form_instance_values (id, form_instance_id, field_key, value, created_at, updated_at) VALUES (?, ?, ?, ?, datetime('now'), datetime('now'))`,
        [crypto.randomUUID(), newId, v.field_key, v.value],
      );
    }
    // Duplicate signatures
    for (const s of sigRows) {
      await db.execute(
        `INSERT INTO form_instance_signatures (id, form_instance_id, field_key, stroke_data, image_url, created_at, updated_at) VALUES (?, ?, ?, ?, ?, datetime('now'), datetime('now'))`,
        [crypto.randomUUID(), newId, s.field_key, s.stroke_data, s.image_url],
      );
    }
    navigate(`/patient/${patientId}/encounters/${encounterId}/forms/${newId}`);
  }, [db, instance, valueRows, sigRows, patientId, encounterId, navigate]);

  const backUrl = `/patient/${patientId}/encounters/${encounterId}?tab=forms`;

  if (!formInstanceId)
    return (
      <div style={{ padding: 'var(--sp-5)', color: 'var(--text-muted)' }}>No form selected.</div>
    );

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: 'var(--sp-4) var(--sp-5)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexShrink: 0,
          borderBottom: '1px solid var(--border)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-3)' }}>
          <button
            onClick={() => navigate(backUrl)}
            style={{
              width: 32,
              height: 32,
              borderRadius: 'var(--radius-sm)',
              border: '1px solid var(--border)',
              background: 'var(--bg-surface)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--text-muted)',
            }}
          >
            <NavIcon name="chevron-left" />
          </button>
          <div>
            <h1
              style={{
                fontSize: 'var(--font-size-lg)',
                fontWeight: 'var(--font-weight-semi)',
                color: 'var(--text)',
                margin: 0,
              }}
            >
              {instance?.form_name ?? 'Loading...'}
            </h1>
            <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
              {isCompleted ? 'Completed' : 'Draft'} &middot; {definition?.name ?? ''}
            </span>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <ModeSwitch mode={viewMode} onChange={setViewMode} primary={primary} />
          {!isCompleted && (
            <button
              onClick={handleComplete}
              style={{
                height: 34,
                padding: '0 16px',
                borderRadius: 'var(--radius-md)',
                border: 'none',
                background: primary,
                color: '#fff',
                fontSize: 'var(--font-size-sm)',
                fontWeight: 'var(--font-weight-semi)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              <NavIcon name="check-circle" /> Complete
            </button>
          )}
          {isCompleted && (
            <button
              onClick={handleCreateNewDraft}
              style={{
                height: 34,
                padding: '0 16px',
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--border)',
                background: 'var(--bg-surface)',
                color: 'var(--text)',
                fontSize: 'var(--font-size-sm)',
                fontWeight: 'var(--font-weight-medium)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              <NavIcon name="plus" /> New Draft from This
            </button>
          )}
        </div>
      </div>

      {/* Completed banner */}
      {isCompleted && (
        <div
          style={{
            margin: 'var(--sp-4) var(--sp-5) 0',
            padding: 'var(--sp-3) var(--sp-4)',
            background: 'var(--color-success-bg)',
            border: '1px solid var(--color-success-border, #bbf7d0)',
            borderRadius: 'var(--radius-md)',
            color: 'var(--color-success-text)',
            fontSize: 'var(--font-size-sm)',
            fontWeight: 'var(--font-weight-medium)',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <NavIcon name="check-circle" /> This form has been completed and is now read-only.
        </div>
      )}

      {/* Content — Form mode or Render mode */}
      <div style={{ flex: 1, overflow: 'auto' }}>
        {viewMode === 'form' ? (
          <div style={{ padding: 'var(--sp-5)' }}>
            <div
              style={{
                maxWidth: 720,
                display: 'flex',
                flexDirection: 'column',
                gap: 'var(--sp-5)',
              }}
            >
              {schema.sections.map((section) => (
                <SectionRenderer
                  key={section.id}
                  section={section}
                  values={valuesMap}
                  signatures={sigMap}
                  onValueChange={handleValueChange}
                  onSignatureSave={handleSignatureSave}
                  readOnly={isCompleted}
                  bindingData={bindingData}
                />
              ))}
              {schema.sections.length === 0 && (
                <div
                  style={{
                    padding: 'var(--sp-8)',
                    textAlign: 'center',
                    color: 'var(--text-muted)',
                    fontSize: 'var(--font-size-sm)',
                  }}
                >
                  {definition ? 'This form has no sections or fields defined.' : 'Loading form...'}
                </div>
              )}
            </div>
          </div>
        ) : (
          <TemplateOverlayView
            pages={templatePages}
            mappings={templateMappings}
            fields={allFields}
            values={valuesMap}
            signatures={sigMap}
            schema={schema}
            bindingData={bindingData}
            onValueChange={handleValueChange}
            onSignatureSave={handleSignatureSave}
            readOnly={isCompleted}
          />
        )}
      </div>
    </div>
  );
}
